import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { RootState } from '@/store/store'
import { FiShield, FiX, FiFileText, FiCheckCircle, FiAlertCircle } from 'react-icons/fi'
import { useVerificationStatus } from '../../hooks/useVerificationStatus'

const DocumentVerificationBanner: React.FC = () => {
    const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
    const { data: verificationData, isFullyVerified, isLoading } = useVerificationStatus()

    const [isDismissed, setIsDismissed] = useState(false)
    const [shouldRender, setShouldRender] = useState(false)

    // Use effect to control when the banner should render to prevent flashing
    useEffect(() => {
        // Don't render if user is not authenticated or banner is dismissed
        if (!isAuthenticated || !user || isDismissed) {
            setShouldRender(false)
            return
        }

        // Don't render while still loading verification data
        if (isLoading) {
            setShouldRender(false)
            return
        }

        // Use real-time verification status from the verification hook
        const isDocumentVerified = isFullyVerified || verificationData?.status === 'verified'

        // Don't render if already verified
        if (isDocumentVerified) {
            setShouldRender(false)
            return
        }

        // Only render if we have verification data OR explicit user verification status
        // This ensures we have actual data to base the decision on
        if (verificationData || user.identityVerification?.status) {
            // Final check: only render if user is explicitly NOT verified
            const identityVerificationStatus = verificationData?.status || user.identityVerification?.status
            const shouldShow = !isDocumentVerified && identityVerificationStatus !== 'verified'
            setShouldRender(shouldShow)
        } else {
            // No verification data available yet, don't render
            setShouldRender(false)
        }
    }, [isAuthenticated, user, isDismissed, isLoading, isFullyVerified, verificationData])

    // Early return if should not render
    if (!shouldRender) {
        return null
    }

    // Get the actual verification status from real-time data
    const identityVerificationStatus = verificationData?.status || user?.identityVerification?.status

    const handleDismiss = () => {
        setIsDismissed(true)
    }

    // Determine banner variant based on verification status
    const getBannerConfig = () => {
        // Check if we have any rejected documents from real-time data
        const hasRejectedDocuments = verificationData?.documents?.some(doc => doc.status === 'rejected')
        const hasPendingDocuments = verificationData?.documents?.some(doc =>
            doc.status === 'pending' || doc.status === 'under_review'
        )

        // Determine effective status based on document states
        let effectiveStatus = identityVerificationStatus
        if (hasRejectedDocuments) {
            effectiveStatus = 'rejected'
        } else if (hasPendingDocuments && !hasRejectedDocuments) {
            effectiveStatus = 'pending'
        } else if (verificationData?.status === 'verified') {
            // If verified status from real-time data, don't show banner
            effectiveStatus = 'verified'
        }

        // Find the most recent rejected document for rejection reason
        const rejectedDocument = verificationData?.documents?.find(doc => doc.status === 'rejected')
        const rejectionReason = rejectedDocument?.rejectionReason || user?.identityVerification?.rejectionReason

        switch (effectiveStatus) {
            case 'pending':
            case 'under_review':
                return {
                    variant: 'info',
                    icon: FiFileText,
                    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
                    borderColor: 'border-blue-200 dark:border-blue-800',
                    textColor: 'text-blue-800 dark:text-blue-300',
                    iconColor: 'text-blue-600 dark:text-blue-400',
                    message: 'Your documents are being reviewed. This typically takes 1-2 business days.',
                    ctaText: 'View Status',
                    ctaVisible: true
                }
            case 'rejected':
                return {
                    variant: 'warning',
                    icon: FiAlertCircle,
                    bgColor: 'bg-red-50 dark:bg-red-900/20',
                    borderColor: 'border-red-200 dark:border-red-800',
                    textColor: 'text-red-800 dark:text-red-300',
                    iconColor: 'text-red-600 dark:text-red-400',
                    message: `Document verification was rejected. ${rejectionReason ? `Reason: ${rejectionReason}` : 'Please resubmit your documents.'}`,
                    ctaText: 'Resubmit Documents',
                    ctaVisible: true
                }
            default: // 'unverified' or undefined
                return {
                    variant: 'default',
                    icon: FiShield,
                    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
                    borderColor: 'border-yellow-200 dark:border-yellow-800',
                    textColor: 'text-yellow-800 dark:text-yellow-300',
                    iconColor: 'text-yellow-600 dark:text-yellow-400',
                    message: 'Verify your identity to build trust and activate the benefits included with your subscription plan. Higher limits depend on your chosen tier.',
                    ctaText: 'Start Verification',
                    ctaVisible: true
                }
        }
    }

    const bannerConfig = getBannerConfig()

    return (
        <div className={`${bannerConfig.bgColor} border-b ${bannerConfig.borderColor}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between py-3">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <bannerConfig.icon className={`h-5 w-5 ${bannerConfig.iconColor}`} />
                        </div>
                        <div className="ml-3 flex-1">
                            <p className={`text-sm ${bannerConfig.textColor}`}>
                                <span className="font-medium">
                                    {(identityVerificationStatus === 'pending' || identityVerificationStatus === 'under_review') && 'Document Review in Progress'}
                                    {identityVerificationStatus === 'rejected' && 'Document Verification Required'}
                                    {(!identityVerificationStatus || identityVerificationStatus === 'unverified') && 'Identity Verification Required'}
                                </span>{' '}
                                {bannerConfig.message}
                                {bannerConfig.ctaVisible && (
                                    <>
                                        {' '}
                                        <Link
                                            to="/dashboard/verification"
                                            className="font-medium underline hover:no-underline inline-flex items-center"
                                        >
                                            {bannerConfig.ctaText}
                                        </Link>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Benefits for unverified users */}
                    {(!identityVerificationStatus || identityVerificationStatus === 'unverified') && (
                        <div className="hidden sm:flex items-center space-x-6 mr-4">
                            <div className="flex items-center space-x-1">
                                <FiCheckCircle className={`h-3 w-3 ${bannerConfig.iconColor}`} />
                                <span className={`text-xs ${bannerConfig.textColor} opacity-80`}>
                                    Tier-Based Limits
                                </span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <FiCheckCircle className={`h-3 w-3 ${bannerConfig.iconColor}`} />
                                <span className={`text-xs ${bannerConfig.textColor} opacity-80`}>
                                    Build Trust
                                </span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <FiCheckCircle className={`h-3 w-3 ${bannerConfig.iconColor}`} />
                                <span className={`text-xs ${bannerConfig.textColor} opacity-80`}>
                                    Verified Badge
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="flex-shrink-0">
                        <button
                            type="button"
                            onClick={handleDismiss}
                            className={`rounded-md ${bannerConfig.bgColor} p-1.5 ${bannerConfig.iconColor} hover:bg-opacity-75 dark:hover:bg-opacity-40 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2`}
                        >
                            <span className="sr-only">Dismiss</span>
                            <FiX className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DocumentVerificationBanner